import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { useState } from "react";
import { useMyMatters } from "@/shared/hooks/useMyMatters";
import { getRecentMatters, searchMatters } from "@/shared/utils/matterSearch";
import {
	matterDisplayLabel,
	matterLookupText,
	matterSearchFields,
} from "@/shared/utils/matterLabels";
import { Check, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { ComboBox } from "@/v2/components/ui/combobox";
import { Button } from "@/v2/components/ui/button";
import {
	ResponsiveDialog,
	ResponsiveDialogContent,
} from "@/v2/components/ui/responsive-dialog";
import { SectionHeading } from "@/v2/components/settings/SectionHeading";
import { SettingGroup } from "@/v2/components/settings/SettingGroup";
import { renderMatterItem } from "@/v2/components/entry-form/pickerItems";

export default function MyMatters() {
	useDocumentTitle("My Matters");
	const {
		matters,
		caseListIds,
		linkedMatters,
		unknownLinkedIds,
		linkedCount,
		isLoading,
		addMatter,
		removeMatter,
	} = useMyMatters();

	const [adding, setAdding] = useState(false);
	const [removing, setRemoving] = useState(false);
	const [pendingRemoval, setPendingRemoval] = useState<{
		matterId: string;
		label: string;
	} | null>(null);

	const handleAdd = async (matterId: string) => {
		setAdding(true);
		const result = await addMatter(matterId);
		if (result.already) {
			toast.info("Already on your list");
		} else if (!result.ok) {
			toast.error(result.error ?? "Failed to add matter");
		} else {
			toast.success("Matter added");
		}
		setAdding(false);
	};

	const confirmRemove = async () => {
		if (!pendingRemoval) return;
		setRemoving(true);
		const result = await removeMatter(pendingRemoval.matterId);
		if (!result.ok) {
			toast.error(result.error ?? "Failed to remove matter");
		} else {
			toast.success("Matter removed");
			setPendingRemoval(null);
		}
		setRemoving(false);
	};

	if (isLoading) {
		return (
			<div>
				<h2 className="text-lg font-semibold mb-4">My Matters</h2>
				<div className="space-y-3">
					<div className="h-10 rounded-lg bg-muted animate-pulse" />
					<div className="h-10 rounded-lg bg-muted animate-pulse" />
				</div>
			</div>
		);
	}

	return (
		<div>
			<h2 className="text-lg font-semibold mb-1">My Matters</h2>
			<p className="text-sm text-muted-foreground mb-4">
				Matters on your list help Hourglass predict the right matter when you
				track time.
			</p>

			<SectionHeading>Add a matter</SectionHeading>
			<div className="py-2">
				<ComboBox
					items={matters}
					value={null}
					onChange={handleAdd}
					fields={matterSearchFields}
					getDefaultItems={getRecentMatters}
					searchItems={searchMatters}
					getKey={(m) => m.matter_id}
					getDisplayLabel={matterDisplayLabel}
					renderItem={(m) => (
						<div className="flex items-center gap-2 min-w-0">
							<div className="min-w-0 flex-1">{renderMatterItem(m)}</div>
							{caseListIds.has(m.matter_id) && (
								<span className="shrink-0 inline-flex items-center gap-1 text-xs text-muted-foreground">
									<Check className="size-3.5" />
									Linked
								</span>
							)}
						</div>
					)}
					placeholder="Add a matter..."
					disabled={adding}
					clearable
				/>
			</div>

			<SectionHeading>
				{linkedCount} matter{linkedCount !== 1 && "s"}
			</SectionHeading>

			{linkedCount === 0 ? (
				<p className="text-sm text-muted-foreground py-4 text-center">
					No matters on your list yet. Add one to start logging time against it.
				</p>
			) : (
				<SettingGroup card>
					{linkedMatters.map((m) => (
						<div
							key={m.matter_id}
							className="flex items-center gap-3 px-3 py-2.5 sm:px-4 sm:py-3"
						>
							<div className="min-w-0 flex-1">
								<p className="text-sm font-medium font-mono truncate">
									{matterLookupText(m)}
								</p>
								<p className="text-sm text-muted-foreground truncate">
									{m.description.trim()}
								</p>
								<p className="text-xs text-muted-foreground/70 truncate">
									{m.client.trim()}
								</p>
							</div>
							<button
								type="button"
								aria-label={`Remove ${matterLookupText(m)}`}
								onClick={() =>
									setPendingRemoval({
										matterId: m.matter_id,
										label: matterLookupText(m),
									})
								}
								className="shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
							>
								<Trash2 className="size-4" />
							</button>
						</div>
					))}
					{unknownLinkedIds.map((id) => (
						<div
							key={id}
							className="flex items-center gap-3 px-3 py-2.5 sm:px-4 sm:py-3"
						>
							<div className="min-w-0 flex-1">
								<p className="text-sm font-medium font-mono truncate">{id}</p>
								<p className="text-xs text-muted-foreground/70">
									Details unavailable
								</p>
							</div>
							<button
								type="button"
								aria-label={`Remove ${id}`}
								onClick={() => setPendingRemoval({ matterId: id, label: id })}
								className="shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
							>
								<Trash2 className="size-4" />
							</button>
						</div>
					))}
				</SettingGroup>
			)}

			<ResponsiveDialog
				open={pendingRemoval !== null}
				onOpenChange={(open) => !open && setPendingRemoval(null)}
			>
				<ResponsiveDialogContent
					title="Remove matter?"
					subtitle={pendingRemoval?.label}
					footer={
						<div className="flex justify-end gap-2">
							<Button
								variant="ghost"
								onClick={() => setPendingRemoval(null)}
								disabled={removing}
							>
								Cancel
							</Button>
							<Button
								variant="destructive"
								onClick={confirmRemove}
								loading={removing}
							>
								Remove
							</Button>
						</div>
					}
				>
					<p className="text-sm text-muted-foreground">
						Hourglass will be less likely to predict this matter when you track
						time.
					</p>
				</ResponsiveDialogContent>
			</ResponsiveDialog>
		</div>
	);
}
